import { useState, useMemo, useEffect } from 'react';
import styles from './UserManagement.module.css';
import AddUser from './AddUser';
import AddUsersBulk from './AddUsersBulk';
import UsersTable from './UsersTable';
import EditUserDialog from './EditUserDialog';
import DeleteUserDialog from './DeleteUserDialog';
import { useTheme } from '../../../hooks/useTheme';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import api from '../../../api/axiosInstance';
import { useLoader } from '../../../hooks/useLoader';
import { AxiosError } from 'axios';
import { RoleDropdownOptions, UserRoles } from '../../../const/UserManagementTypes';
import { useAuth } from '../../../hooks/AuthProvider';
import { KEYCLOAK_URL } from '../../../config';

type User = { email: string; roles: string[]; role: string; id: string };

interface UserManagementProps {
  activeTab: number;
}
const UserManagement = ({ activeTab }: UserManagementProps) => {
  const { theme } = useTheme();
  const { addNotifications } = useSwobNotifications();
  const { hasRole } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [filterEmail, setFilterEmail] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [sortAttr, setSortAttr] = useState<'email' | 'role' | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('10');

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUserIdx, setSelectedUserIdx] = useState<number | null>(null);
  const { showLoader, hideLoader } = useLoader();

  const keycloakAdminUrl = `${KEYCLOAK_URL}/admin/master/console`;

  const fetchData = async () => {
    showLoader();
    try {
      const response = await api.get('/api/keycloak/users');
      setUsers(
        response.data.map((user: { roles: string[] }) => ({
          ...user,
          role: user.roles.includes(UserRoles.Admin) ? RoleDropdownOptions.Admin : RoleDropdownOptions.Operator
        }))
      );
    } catch (error: unknown) {
      let errorMsg = `An error occurred while fetching user management information.`;
      if (error instanceof AxiosError) {
        errorMsg = error?.response?.data?.message || error?.message;
      }
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: errorMsg,
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, 1000);
    }
  };

  useEffect(() => {
    // Fetches information on tab selection
    if (activeTab === 0) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const filteredUsers = useMemo(() => {
    return users.filter(
      (u) =>
        (!filterEmail || (u.email || '').toLowerCase().includes(filterEmail.toLowerCase())) &&
        (!filterRole || (u.role || '').toLowerCase().includes(filterRole.toLowerCase()))
    );
  }, [users, filterEmail, filterRole]);

  const sortedUsers = useMemo(() => {
    if (!sortAttr || !sortOrder) return filteredUsers;
    const sorted = [...filteredUsers].sort((a, b) => {
      const valA = a[sortAttr] ? a[sortAttr].toLowerCase() : '';
      const valB = b[sortAttr] ? b[sortAttr].toLowerCase() : '';
      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredUsers, sortAttr, sortOrder]);

  const numPages = Math.max(1, Math.ceil(sortedUsers.length / parseInt(pageSize)));
  const pagedUsers = useMemo(() => {
    const start = (page - 1) * parseInt(pageSize);
    return sortedUsers.slice(start, start + parseInt(pageSize));
  }, [sortedUsers, page, pageSize]);

  const handleAddUser = (user: User) => {
    setUsers((prev) => [...prev, user]);
    setPage(1);
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAddUsers = (_users: User[]) => {
    fetchData();
  };
  const handleEditUser = (idx: number) => {
    setSelectedUserIdx(idx);
    setEditDialogOpen(true);
  };
  const handleDeleteUser = (idx: number) => {
    setSelectedUserIdx(idx);
    setDeleteDialogOpen(true);
  };
  const handleSaveUser = (user: User) => {
    setUsers((prev) => prev.map((u) => (u.id === user.id ? user : u)));
    setEditDialogOpen(false);
    setSelectedUserIdx(null);
  };
  const handleDeleteConfirmed = () => {
    if (selectedUserIdx !== null) {
      setUsers((prev) => prev.filter((usr) => usr.id !== pagedUsers[selectedUserIdx].id));
    }
    console.dir(pagedUsers);
    setDeleteDialogOpen(false);
    setSelectedUserIdx(null);
    if (pagedUsers.length === 1 && page > 1) setPage(1);
  };
  const handleCancelDialog = () => {
    setEditDialogOpen(false);
    setDeleteDialogOpen(false);
    setSelectedUserIdx(null);
  };

  const handleSort = (order: 'asc' | 'desc', attr: 'email' | 'role') => {
    setSortAttr(attr);
    setSortOrder(order);
  };
  const handlePageChange = (newPage: number) => setPage(newPage);
  const handlePageSizeChange = (size: string) => {
    setPageSize(size);
    setPage(1);
  };
  const handleFilterEmail = (val: string) => {
    setFilterEmail(val);
    setPage(1);
  };
  const handleFilterRole = (val: string) => {
    setFilterRole(val);
    setPage(1);
  };

  return (
    <div className={`${styles.container} ${styles.containerPadding} ${theme ? styles.containerLight : styles.containerDark}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div className={styles.title}>User Management</div>
          <div className={styles.description}>Add users and assign roles to manage access and permissions.</div>
        </div>
        {hasRole('admin') && (
          <button className="btn primary" onClick={() => window.open(`${keycloakAdminUrl}`, '_blank')}>
            Advanced Settings
          </button>
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.leftColumn}>
          <div className={styles.panelTitle}>Adding the users</div>
          <AddUser onAdd={handleAddUser} existingEmails={users.map((u) => u.email)} />
          <AddUsersBulk onAdd={handleAddUsers} />
        </div>
        <div className={styles.rightColumn}>
          <div className={styles.panelTitle}>Users List</div>
          <div className={styles.panelSubtitle}>Manage user configuration. You can edit or remove users.</div>
          {users.length > 0 ? (
            <UsersTable
              users={pagedUsers}
              page={page}
              pageSize={pageSize}
              numPages={numPages}
              filterEmail={filterEmail}
              filterRole={filterRole}
              sortAttr={sortAttr ?? undefined}
              sortOrder={sortOrder ?? undefined}
              onEdit={handleEditUser}
              onDelete={handleDeleteUser}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onSort={handleSort}
              onFilterEmail={handleFilterEmail}
              onFilterRole={handleFilterRole}
            />
          ) : (
            <div className={styles.emptyState}>
              <i className="icon icon-info" />
              <div className={styles.emptyStateTitle}>There is no users</div>
              <div className={styles.emptyStateSubtitle}>Please add a user or add users in Bulk uploading a file.</div>
            </div>
          )}
          <EditUserDialog
            open={editDialogOpen}
            initialUser={selectedUserIdx !== null ? pagedUsers[selectedUserIdx] : null}
            onSave={handleSaveUser}
            onClose={handleCancelDialog}
          />
          <DeleteUserDialog
            open={deleteDialogOpen}
            initialUser={selectedUserIdx !== null ? pagedUsers[selectedUserIdx] : null}
            onDelete={handleDeleteConfirmed}
            onCancel={handleCancelDialog}
          />
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
